import {
  AggregatedCreditNote,
  AggregatedLineItemGroup,
  ServerLineItem
} from 'CreditNotes/types'
import differenceBy from 'lodash/differenceBy'
import { removeUnselectedLineItems } from 'CreditNotes/utils/lineItemGroup'
import { useMutation } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'
import { useCallback } from 'react'
import { developerLog } from '@sequencehq/utils'
import { dequal } from 'dequal'
import { useDebouncedCallback } from 'use-debounce'

export const useSaveAggregatedCreditNote = (creditNoteId: string) => {
  const putCreditNoteMutation = useMutation(
    dashboardv99990101Client.putCreditNote
  )

  const postLineItemMutation = useMutation(
    dashboardv99990101Client.postCreditNoteLineItem
  )

  const putLineItemMutation = useMutation(
    dashboardv99990101Client.putCreditNoteLineItem
  )

  const deleteLineItemGroupMutation = useMutation(
    dashboardv99990101Client.deleteCreditNoteLineItemGroup
  )

  const deleteLineItemMutation = useMutation(
    dashboardv99990101Client.deleteCreditNoteLineItem
  )

  const { displayNotification } = useNotifications()

  const performSave = useCallback(
    async (
      lineItemGroupDiffs: ReturnType<typeof diffLineItemGroups>,
      billingPeriodsDiff: ReturnType<typeof diffBillingPeriods>,
      accountingDateDiff: ReturnType<typeof diffAccountingDate>,
      taxDateDiff: ReturnType<typeof diffTaxDate>,
      clientCreditNote: AggregatedCreditNote
    ) => {
      const hasLineItemChanges =
        lineItemGroupDiffs.lineItemsToDelete.length > 0 ||
        lineItemGroupDiffs.lineItemsToAdd.length > 0 ||
        lineItemGroupDiffs.lineItemsToUpdate.length > 0 ||
        lineItemGroupDiffs.groupsToDelete.length > 0
      const hasChanges =
        hasLineItemChanges ||
        Boolean(billingPeriodsDiff) ||
        Boolean(accountingDateDiff) ||
        Boolean(taxDateDiff)

      if (!hasChanges) {
        return
      }

      const deletions = lineItemGroupDiffs.lineItemsToDelete.map(id =>
        deleteLineItemMutation.mutateAsync({ id, creditNoteId })
      )
      const additions = lineItemGroupDiffs.lineItemsToAdd.map(item =>
        postLineItemMutation.mutateAsync({ body: item, creditNoteId })
      )
      const updates = lineItemGroupDiffs.lineItemsToUpdate.map(item =>
        putLineItemMutation.mutateAsync({
          body: item,
          id: item.id,
          creditNoteId
        })
      )
      const groupDeletions = lineItemGroupDiffs.groupsToDelete.map(id =>
        deleteLineItemGroupMutation.mutateAsync({ id, creditNoteId })
      )

      try {
        await Promise.all([
          ...deletions,
          ...additions,
          ...updates,
          ...groupDeletions
        ])

        if (billingPeriodsDiff || accountingDateDiff || taxDateDiff) {
          await putCreditNoteMutation.mutateAsync({
            id: creditNoteId,
            body: {
              billingPeriodStart:
                billingPeriodsDiff?.billingPeriodStart ??
                clientCreditNote.billingPeriodStart,
              billingPeriodEnd:
                billingPeriodsDiff?.billingPeriodEnd ??
                clientCreditNote.billingPeriodEnd,
              accountingDate:
                accountingDateDiff?.accountingDate ??
                clientCreditNote.accountingDate,
              taxDate: taxDateDiff?.taxDate ?? clientCreditNote.taxDate
            }
          })
        }

        // Fixed id so rapid debounced saves reuse a single toast instead
        // of stacking
        displayNotification('Credit note updated', {
          type: 'success',
          id: 'creditNote.autosave'
        })
      } catch (e) {
        developerLog('[CreditNote] perform save error', e)
        const message = getApiErrorMessage(e, 'Failed to update credit note')
        displayNotification(message, {
          type: 'error',
          id: 'creditNote.autosave'
        })
      }
    },
    [
      creditNoteId,
      deleteLineItemMutation,
      deleteLineItemGroupMutation,
      postLineItemMutation,
      putLineItemMutation,
      putCreditNoteMutation,
      displayNotification
    ]
  )

  const callback = useCallback(
    (
      serverCreditNote: AggregatedCreditNote,
      clientCreditNote: AggregatedCreditNote
    ) => {
      const lineItemGroupDiffs = diffLineItemGroups(
        serverCreditNote.lineItemGroups.map(removeUnselectedLineItems),
        clientCreditNote.lineItemGroups.map(removeUnselectedLineItems)
      )

      const billingPeriodsDiff = diffBillingPeriods({
        serverCreditNote,
        clientCreditNote
      })

      const accountingDateDiff = diffAccountingDate({
        serverCreditNote,
        clientCreditNote
      })

      const taxDateDiff = diffTaxDate({
        serverCreditNote,
        clientCreditNote
      })

      performSave(
        lineItemGroupDiffs,
        billingPeriodsDiff,
        accountingDateDiff,
        taxDateDiff,
        clientCreditNote
      )
    },
    [performSave]
  )

  return useDebouncedCallback(callback, 1000)
}

const diffBillingPeriods = ({
  serverCreditNote,
  clientCreditNote
}: {
  serverCreditNote: AggregatedCreditNote
  clientCreditNote: AggregatedCreditNote
}) => {
  const hasBillingPeriodStartChanged =
    serverCreditNote.billingPeriodStart !== clientCreditNote.billingPeriodStart
  const hasBillingPeriodEndChanged =
    serverCreditNote.billingPeriodEnd !== clientCreditNote.billingPeriodEnd

  if (hasBillingPeriodStartChanged || hasBillingPeriodEndChanged) {
    return {
      billingPeriodStart: clientCreditNote.billingPeriodStart,
      billingPeriodEnd: clientCreditNote.billingPeriodEnd
    }
  }

  return undefined
}

const diffAccountingDate = ({
  serverCreditNote,
  clientCreditNote
}: {
  serverCreditNote: AggregatedCreditNote
  clientCreditNote: AggregatedCreditNote
}) => {
  const hasAccountingDateChanged =
    serverCreditNote.accountingDate !== clientCreditNote.accountingDate

  if (hasAccountingDateChanged) {
    return {
      accountingDate: clientCreditNote.accountingDate
    }
  }

  return undefined
}

const diffTaxDate = ({
  serverCreditNote,
  clientCreditNote
}: {
  serverCreditNote: AggregatedCreditNote
  clientCreditNote: AggregatedCreditNote
}) => {
  const hasTaxDateChanged =
    serverCreditNote.taxDate !== clientCreditNote.taxDate

  if (hasTaxDateChanged) {
    return {
      taxDate: clientCreditNote.taxDate
    }
  }

  return undefined
}

export const diffLineItemGroups = (
  storedLineItemGroups: AggregatedLineItemGroup[],
  changedLineItemGroups: AggregatedLineItemGroup[]
) => {
  const diffs = {
    groupsToDelete: [] as string[],
    lineItemsToDelete: [] as string[],
    lineItemsToAdd: [] as Omit<ServerLineItem, 'id'>[],
    lineItemsToUpdate: [] as ServerLineItem[]
  }

  for (const storedGroup of storedLineItemGroups) {
    const changedGroup = changedLineItemGroups.find(
      g => g.id === storedGroup.id
    )
    if (!changedGroup) {
      diffs.groupsToDelete.push(storedGroup.id)
      diffs.lineItemsToDelete.push(...storedGroup.lineItems.map(i => i.id))
      continue
    }

    diffs.lineItemsToDelete.push(
      ...differenceBy(storedGroup.lineItems, changedGroup.lineItems, 'id').map(
        item => item.id
      )
    )

    for (const item of changedGroup.lineItems) {
      const storedItem = storedGroup.lineItems.find(i => i.id === item.id)
      if (!storedItem) {
        const { id, ...itemToAdd } = item
        diffs.lineItemsToAdd.push(itemToAdd)
        continue
      }
      if (!dequal(item, storedItem)) {
        diffs.lineItemsToUpdate.push(item)
      }
    }
  }

  return diffs
}
